<template>
  <header dir="rtl" class="mt-0 md:mt-10 text-white select-none relative z-20">
    <div class="max-w-7xl mx-auto px-2 md:px-0 py-3 md:py-5">
      
      <!-- Top Bar -->
      <div class="flex items-center justify-between gap-3 mb-6">
        <div class="flex items-center gap-3">
          <div class="text-lg md:text-2xl font-black tracking-tighter text-white">
           سامانه طلا و سکه سالاری
          </div>
          <div class="hidden sm:flex items-center gap-2 rounded-full bg-[#1e2623]/80 backdrop-blur px-3 py-1 text-xs border border-[#334D44]/50">
            <span class="relative flex h-2 w-2">
              <span v-if="connected" class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2" :class="connected ? 'bg-emerald-500' : 'bg-yellow-500'"></span>
            </span>
            <span class="font-medium text-white/80">{{ connected ? 'متصل' : 'در حال اتصال' }}</span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- Notification Button -->
          <div class="relative hidden md:block" ref="notifRef">
            <button
              @click="toggleNotif"
              class="relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#1e2623]/50 border border-[#334D44]/50 hover:bg-[#334D44]/30 hover:text-emerald-300 transition-all duration-300"
            >
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.8 23.8 0 0 0 5.454-1.31A8.97 8.97 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.97 8.97 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.26 24.26 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"/>
              </svg>
              <span v-if="unreadCount>0" class="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-emerald-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-[#0f1417]">
                {{ unreadCount }}
              </span>
            </button>

            <transition name="fade">
              <div v-if="notifOpen" class="absolute left-0 mt-2 w-80 z-50 rounded-2xl bg-[#141a1e]/95 backdrop-blur-xl border border-[#334D44] shadow-2xl p-1">
                <div class="flex items-center justify-between p-3 border-b border-white/5">
                  <span class="text-xs font-bold text-white/90">اعلان‌ها</span>
                  <button class="text-[10px] text-emerald-400 hover:text-emerald-300 transition" @click="markAllRead">خواندم</button>
                </div>
                <div class="max-h-60 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                  <div v-for="n in notifications" :key="n._id" class="p-2 rounded-lg hover:bg-white/5 transition border border-transparent hover:border-white/5">
                    <div class="flex justify-between items-start mb-1">
                      <span class="text-xs font-semibold text-white/90 truncate ml-2">{{ n.title }}</span>
                      <span class="text-[9px] text-white/40 whitespace-nowrap">{{ formatTime(n.createdAt) }}</span>
                    </div>
                    <p class="text-[10px] text-white/60 line-clamp-2 leading-relaxed">{{ n.text }}</p>
                  </div>
                  <div v-if="notifications.length===0" class="text-center py-4 text-xs text-white/30">پیامی نیست</div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Profile Button -->
          <div class="relative" ref="profileRef">
            <button
              @click="toggleProfile"
              class="flex items-center gap-2 h-10 px-3 rounded-xl bg-gradient-to-r from-[#1e2623] to-[#141a1e] border border-[#334D44]/50 hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <div class="h-6 w-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center border border-emerald-500/30">
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <span class="text-xs font-medium group-hover:text-white transition">حساب کاربری</span>
            </button>
            
            <transition name="fade">
              <div v-if="profileOpen" class="absolute left-0 mt-2 w-56 z-50 rounded-xl bg-[#141a1e]/95 backdrop-blur-xl border border-[#334D44] shadow-xl overflow-hidden py-2 max-h-[80vh] overflow-y-auto custom-scrollbar">
                
                <div class="px-4 py-1.5 text-[10px] text-white/30 font-bold tracking-wider">عمومی</div>
                <router-link to="/user" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">داشبورد</router-link>
                <router-link to="/user/price" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">تابلو قیمت</router-link>
                <router-link to="/user/wallet/me" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">دارایی‌های من</router-link>

                <div class="h-px bg-white/5 my-1 mx-3"></div>
                <div class="px-4 py-1.5 text-[10px] text-white/30 font-bold tracking-wider">مالی</div>
                
                <router-link to="/user/deposit" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">لیست واریز ها </router-link>
                <router-link to="/user/withdrawal" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">برداشت وجه</router-link>
                <router-link to="/user/wallet" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition"> افزایش موجودی</router-link>
                <router-link to="/user/card" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">کارت‌های بانکی</router-link>
                <router-link to="/user/history" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">تاریخچه تراکنش‌ها</router-link>

                <div class="h-px bg-white/5 my-1 mx-3"></div>
                <div class="px-4 py-1.5 text-[10px] text-white/30 font-bold tracking-wider">حساب کاربری</div>

                <router-link to="/user/profile" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">پروفایل کاربری</router-link>
                <router-link to="/user/verify" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">احراز هویت</router-link>
                <router-link to="/user/ref" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">معرفی دوستان</router-link>
                
                <div class="h-px bg-white/5 my-1 mx-3"></div>

                <router-link to="/user/support" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">پشتیبانی</router-link>
                <router-link to="/user/notification" class="block px-4 py-2 text-xs text-white/80 hover:text-white hover:bg-white/5 transition">اعلان‌ها</router-link>

                <div class="h-px bg-white/5 my-1 mx-3"></div>
                <button @click="logout" class="block w-full text-right px-4 py-2 text-xs text-rose-400 hover:bg-rose-500/10 transition">خروج از حساب</button>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Cards Section (Removed Notification Panel) -->
      <div class="w-full mb-4">
        <!-- 3 Cards Grid -->
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-4">
          
          <!-- 1. Wallet Balance (With Toggle) -->
          <div class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a2220] to-[#12181b] p-3 md:p-4 border border-[#334D44]/40 group hover:border-emerald-500/30 transition-colors col-span-2 sm:col-span-1 shadow-lg">
            <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-transparent opacity-50"></div>
            <div class="flex justify-between items-start mb-2">
              <span class="text-[10px] md:text-xs font-medium text-emerald-400/80">موجودی کیف پول</span>
              <div class="flex items-center gap-1.5">
                <button 
                  @click="showRial = !showRial"
                  class="px-2 py-0.5 rounded-md bg-white/5 hover:bg-white/10 text-[9px] text-white/60 hover:text-white transition-colors border border-white/5"
                >
                  {{ showRial ? 'تومان' : 'ریال' }}
                </button>
                <router-link to="/user/wallet" class="p-1 rounded bg-white/5 hover:bg-emerald-500/20 text-emerald-400 transition">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                </router-link>
              </div>
            </div>
            <div class="flex flex-col">
              <span class="text-lg md:text-2xl font-black tracking-tight text-white mb-0.5 transition-all duration-300">
                {{ nfmt(showRial ? walletToman * 10 : walletToman) }}
              </span>
              <span class="text-[10px] text-white/40 transition-all duration-300">{{ showRial ? 'ریال' : 'تومان' }}</span>
            </div>
          </div>

          <!-- 2. Gold Balance (Gram) -->
          <div class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a2220] to-[#12181b] p-3 md:p-4 border border-[#334D44]/40 hover:border-amber-400/30 transition-colors shadow-lg">
            <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-transparent opacity-50"></div>
            <div class="flex justify-between items-start mb-2">
              <span class="text-[10px] md:text-xs font-medium text-amber-400/80">موجودی طلا</span>
              <router-link to="/user/wallet" class="p-1 rounded bg-white/5 hover:bg-amber-500/20 text-amber-400 transition">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              </router-link>
            </div>
            <div class="flex flex-col">
              <span class="text-lg md:text-2xl font-black tracking-tight text-white mb-0.5">{{ balanceGram }}</span>
              <span class="text-[10px] text-white/40">گرم</span>
            </div>
          </div>

          <!-- 3. Market Price -->
          <div class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a2220] to-[#12181b] p-3 md:p-4 border border-[#334D44]/40 shadow-lg">
            <div class="flex justify-between items-center mb-2">
              <span class="text-[10px] md:text-xs font-medium text-white/50">مظنه ۱۸ عیار</span>
              <span class="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-black/30 border border-white/5" :class="trendColor">
                <svg v-if="trend === 'up'" class="w-2 h-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m18 15-6-6-6 6"/></svg>
                <svg v-else-if="trend === 'down'" class="w-2 h-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m6 9 6 6 6-6"/></svg>
                <span v-else class="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
              </span>
            </div>
            <div class="flex flex-col">
              <span class="text-lg md:text-2xl font-black tracking-tight text-emerald-300 mb-0.5 drop-shadow-[0_0_8px_rgba(52,211,153,0.15)]">
                {{ goldPriceNumber > 0 ? nfmt(goldPriceNumber) : '---' }}
              </span>
              <span class="text-[10px] text-white/40">تومان / گرم</span>
            </div>
          </div>

        </div>
      </div>

      <!-- Ticker / Info Bar -->
      <div class="mt-3 relative rounded-xl bg-[#141a1e] border border-[#334D44]/40 overflow-hidden h-9 flex items-center shadow-md">
        <div class="absolute right-0 top-0 bottom-0 z-10 bg-gradient-to-l from-[#141a1e] via-[#141a1e] to-transparent px-3 flex items-center text-[10px] font-bold text-emerald-400">
          <div class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse ml-2"></div>
          بازار زنده
        </div>
        <div class="ticker-wrap w-full">
          <div class="ticker">
            <span class="item">طلای ۱۸: <b class="text-white">{{ goldPriceNumber ? nfmt(goldPriceNumber) : '---' }}</b></span>
            <span class="item">روند: <b :class="trend === 'up' ? 'text-green-400' : 'text-red-400'">{{ trendLabel }}</b></span>
            <span class="item">بروزرسانی: <b>{{ lastUpdateLabel }}</b></span>
            
            <span class="item">طلای ۱۸: <b class="text-white">{{ goldPriceNumber ? nfmt(goldPriceNumber) : '---' }}</b></span>
            <span class="item">روند: <b :class="trend === 'up' ? 'text-green-400' : 'text-red-400'">{{ trendLabel }}</b></span>
            <span class="item">بروزرسانی: <b>{{ lastUpdateLabel }}</b></span>
          </div>
        </div>
      </div>

    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { io } from 'socket.io-client'
import { useRouter } from 'vue-router'

const router = useRouter()
const WS_URL = process.env.VUE_APP_API_WS || ''
const API_URL = process.env.VUE_APP_API_URL || ''

const connected = ref(false)
const goldPrice = ref(null)
const prevPrice = ref(null)
const lastUpdate = ref(null)
const balanceGram = ref(0) // موجودی گرم طلا
const walletToman = ref(0) // موجودی تومان کیف پول
const showRial = ref(false) // وضعیت نمایش ریال/تومان
const notifications = ref([])
const notifOpen = ref(false)
const profileOpen = ref(false)
const notifRef = ref(null)
const profileRef = ref(null)
let notifTimer = null
let socket = null

function toggleNotif() {
  notifOpen.value = !notifOpen.value
  if (notifOpen.value) profileOpen.value = false
}

function toggleProfile() {
  profileOpen.value = !profileOpen.value
  if (profileOpen.value) notifOpen.value = false
}

function markAllRead() {
  notifications.value = notifications.value.map(n => ({ ...n, read: true }))
}

const unreadCount = computed(() => notifications.value.filter(n => !n.read).length)

function onClickOutside(e) {
  if (notifOpen.value && notifRef.value && !notifRef.value.contains(e.target)) notifOpen.value = false
  if (profileOpen.value && profileRef.value && !profileRef.value.contains(e.target)) profileOpen.value = false
}

function onEscape(e) {
  if (e.key === 'Escape') {
    notifOpen.value = false
    profileOpen.value = false
  }
}

async function fetchNotifications() {
  try {
    const r = await axios.get(`${API_URL}/user/getnotif`)
    const arr = Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : []
    notifications.value = arr.map(x => ({
      _id: x._id || Math.random().toString(36).slice(2),
      title: x.title || 'پیام سیستم',
      text: x.text || '',
      createdAt: x.createdAt || x.date || new Date().toISOString(),
      read: !!x.read
    }))
  } catch {
    notifications.value = []
  }
}

// 1. دریافت موجودی تومان از پروفایل
async function fetchProfile() {
  try {
    const res = await axios.get(`${API_URL}/user/profile`)
    // فرض بر اینکه API آبجکت user را برمی‌گرداند که فیلد toman دارد
    if (res.data && res.data.user) {
        walletToman.value = res.data.user.toman || 0
    }
  } catch (e) {
    console.error('Error fetching profile:', e)
  }
}

// 2. دریافت موجودی طلا از لیست دارایی‌های کیف پول
async function fetchWallet() {
  try {
    const res = await axios.get(`${API_URL}/user/wallet/assets`)
    const assets = res.data.data || res.data || []
    
    // پیدا کردن موجودی سکه یا طلای آبشده (YekGram18)
    // اگر نام در دیتابیس فرق دارد (مثلا gold18)، اینجا باید اضافه شود
    const goldAsset = Array.isArray(assets) 
      ? assets.find(a => a.name === 'YekGram18' || a.name === 'gold18')
      : null
      
    balanceGram.value = goldAsset ? Number(goldAsset.balance) : 0
  } catch (e) {
    console.error('Error fetching wallet:', e)
  }
}

function parseTimeRead(s) {
  if (!s || typeof s !== 'string') return new Date()
  if (s.includes('T')) return new Date(s)
  const parts = s.split(' ')
  if (parts.length !== 2) return new Date()
  const [d, t] = parts
  const [yy, mm, dd] = d.split('/').map(n => parseInt(n, 10))
  const [hh, mi, ss] = t.split(':').map(n => parseInt(n, 10))
  const dt = new Date(yy, (mm || 1) - 1, dd || 1, hh || 0, mi || 0, ss || 0)
  return Number.isNaN(dt.getTime()) ? new Date() : dt
}

function applyData(data) {
  if (data.timeRead) lastUpdate.value = parseTimeRead(data.timeRead)
  else lastUpdate.value = new Date()

  if (data.user && data.user.gold18) {
    const price = data.user.gold18.buy
    if (price && price !== goldPrice.value) {
      prevPrice.value = goldPrice.value
      goldPrice.value = price
    }
  }
}

onMounted(async () => {
  try {
    socket = io(WS_URL, { withCredentials: true, transports: ['websocket'] })
    socket.on('connect', () => { connected.value = true })
    socket.on('disconnect', () => { connected.value = false })
    socket.on('prices:init', applyData)
    socket.on('prices:update', applyData)
  } catch (e) {
    console.log(e);
  }

  // فراخوانی همزمان دیتاها
  await Promise.all([
      fetchNotifications(),
      fetchProfile(), // تومان
      fetchWallet()   // گرم طلا
  ])
  
  notifTimer = setInterval(fetchNotifications, 60000)
  
  document.addEventListener('click', onClickOutside)
  document.addEventListener('keydown', onEscape)
})

onBeforeUnmount(() => {
  if (socket) socket.disconnect()
  if (notifTimer) clearInterval(notifTimer)
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('keydown', onEscape)
})

async function logout() {
  localStorage.removeItem('token')
  delete axios.defaults.headers.common.Authorization
  profileOpen.value = false
  try { await router.push('/login') } catch { window.location.href = '/login' }
}

const goldPriceNumber = computed(() => Number(goldPrice.value) || 0)
const lastUpdateLabel = computed(() => !lastUpdate.value ? '...' : lastUpdate.value.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }))

const trend = computed(() => {
  if (!prevPrice.value || !goldPrice.value) return 'flat'
  return goldPrice.value > prevPrice.value ? 'up' : goldPrice.value < prevPrice.value ? 'down' : 'flat'
})

const trendLabel = computed(() => trend.value === 'up' ? 'افزایشی' : trend.value === 'down' ? 'کاهشی' : 'ثابت')
const trendColor = computed(() => trend.value === 'up' ? 'text-emerald-400' : trend.value === 'down' ? 'text-red-400' : 'text-gray-400')

function nfmt(n) {
  return new Intl.NumberFormat('fa-IR').format(Math.round(Number(n) || 0))
}

function formatTime(d) {
  if (!d) return ''
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toLocaleDateString('fa-IR') === new Date().toLocaleDateString('fa-IR') 
    ? dt.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }) 
    : dt.toLocaleDateString('fa-IR')
}
</script>

<style scoped>
.ticker-wrap { overflow: hidden; white-space: nowrap; mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent); }
.ticker { display: inline-block; animation: ticker 40s linear infinite; }
.ticker:hover { animation-play-state: paused; }
.item { display: inline-block; padding: 0 1.5rem; font-size: 0.75rem; color: rgba(255,255,255,0.6); }
@keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
.fade-enter-active, .fade-leave-active { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-5px) scale(0.98); }
.custom-scrollbar::-webkit-scrollbar { width: 3px; }
.custom-scrollbar::-webkit-scrollbar-track { bg: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #334D44; border-radius: 4px; }
</style>